import { PropType, useSlots, ref, computed } from 'vue'

export interface Props {
  isLink: boolean,
  href: string,
  name: string,
  emitName: string,
  isFontSmall: boolean,
  isFontBold: boolean,
  narrowMargin: boolean,
  disabled: boolean,
  prevent: boolean,
  stop: boolean,
  target: '_blank' | '_self' | '_top' | '_parent',
  width: number | undefined,
  hasShadow: boolean,
}

export const defaultProps = {
  /**
   * HTML の a 要素（アンカー）かどうか
   */
  isLink: {
    type: Boolean,
    default: true,
  },

  href: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: '',
  },

  /**
   * クリックした際に発行する emit の名称
   */
  emitName: {
    type: String,
    default: 'click',
  },

  isFontSmall: {
    type: Boolean,
    default: false,
  },

  isFontBold: {
    type: Boolean,
    default: false,
  },

  /**
   * ボタンの大きさをデフォルトより一回り大きくするかどうか
   */
  isLarge: {
    type: Boolean,
    default: false,
  },

  /**
   * ボタンの上マージンを狭めるかどうか
   */
  narrowMargin: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  /**
   * クリック時に prevent default させるかどうか
   */
  prevent: {
    type: Boolean,
    default: false,
  },

  /**
   * クリック時に stop propagation させるかどうか
   */
  stop: {
    type: Boolean,
    default: false,
  },

  /**
   * リンクだった場合の target 属性値
   */
  target: {
    type: String as PropType<'_blank' | '_self' | '_top' | '_parent'>,
    default: '_self',
  },

  /**
   * ボタン幅を自身のコンテンツ量に依存した幅にするかどうか
   *
   * @description
   *  true: ボタン自身の文字サイズ・文字数に準じたサイズになる
   *  false: 親 container の inner width いっぱいに広がる（通常の display: block の挙動）
   */
  autoWidth: {
    type: Boolean,
    default: false,
  },

  /**
   * 幅設定
   * @description 基本的にはレイアウトラッパーの DOM を用意してレイアウト側で幅をコントロールすること
   *              やむを得ずラッパー側で幅が指定できない（auto、もしくは 100% にせざるを得ない）場合に本 props を使用し、幅の設定を要素側に持たせる
   */
  width: {
    type: Number,
    default: undefined,
  },

  /**
   * ボタン hover 時にシャドウのアニメーションをかけるかどうか
   */
  hasShadow: {
    type: Boolean,
    default: false,
  },
}

export function defaultFunction(props: any & Props, emit: (event: string, ...args: any[]) => void) {
  const slots = useSlots()
  const wrapper = ref<HTMLElement>()
  const buttonTagName = computed(() => {
    return props.isLink ? 'a' : 'button'
  })

  const hasSlot = computed(() => {
    return !!slots.default && slots.default().some(vnode => vnode.children || vnode.props)
  })

  const runEvent = (e: Event) => {
    if (props.prevent) e.preventDefault()
    if (props.stop) e.stopPropagation()
    emit(props.emitName)
  }

  return {
    // data
    wrapper,

    // computed
    buttonTagName,
    hasSlot,

    // methods
    runEvent,
  }
}
